<template>
  <div class="d-flex flex-wrap align-items-start location-picker">
    <!-- Region select -->
    <div class="picker-field mr-2 mb-2">
      <b-form-group
        label="Region"
        label-size="sm"
        label-class=" mb-1 font-weight-bold"
      >
        <v-select
          :options="locations"
          label="name"
          :reduce="loc => loc.id"
          v-model="localRegions"
          :multiple="true"
          @input="onRegionsChange"
          :placeholder="locations.length ? 'Select region(s)...' : 'No regions available'"
        />
      </b-form-group>
    </div>

    <!-- Location select (children of selected regions) -->
    <div class="picker-field mb-2" v-if="showLocations">
      <b-form-group
        label="District"
        label-size="sm"
        label-class=" mb-1 font-weight-bold"
      >
        <v-select
          :options="locationsForSelectedRegions"
          label="name"
          :reduce="d => d.id"
          v-model="localLocations"
          :multiple="true"
          @input="onLocationsChange"
          :placeholder="locationsForSelectedRegions.length ? 'Select location(s)...' : 'No locations available'"
        />
      </b-form-group>
    </div>
  </div>
</template>

<script>
/*
  IMPORTANT: Ensure you have installed vue-select:
    npm install vue-select

  And import its CSS in your project (e.g. in main.js or App.vue):
    import 'vue-select/dist/vue-select.css'
*/
import { ref, onMounted, watch } from '@vue/composition-api'
import vSelect from 'vue-select'
import {
  BRow,
  BCol,
  BCard,
  BCardHeader,
  BCardBody,
  BTable,
  BPagination,
  BForm,
  BFormGroup,
  BButton,
  BFormSelect,
  BFormSelectOption,
  BFormInput,
  BDropdown,
  BDropdownItem,
  BSpinner,
} from "bootstrap-vue";

export default {
  name: 'LocationPicker',
  components: { 'v-select': vSelect,     BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BTable,
    BPagination,
    BForm,
    BFormGroup,
    BButton,
    BFormSelect,
    BFormSelectOption,
    BFormInput,
    BDropdown,
    BDropdownItem,
    BSpinner },

  props: {
    // Parent should pass institute.region_id (array) and institute.location_id (array)
    regionValue: {
      type: Array,
      default: () => [],
    },
    locationValue: {
      type: Array,
      default: () => [],
    },
    storageKey: {
      type: String,
      default: 'clientLocations',
    },
  },
  setup(props, { emit }) {
    const locations = ref([]) // top-level regions from localStorage
    const locationsForSelectedRegions = ref([]) // merged children of selected regions

    // local v-select models
    const localRegions = ref([...props.regionValue])
    const localLocations = ref([...props.locationValue])
    const showLocations = ref(false)

    const loadLocationsFromLocalStorage = () => {
      try {
        const stored =
          localStorage.getItem(props.storageKey) ||
          localStorage.getItem('userdata') ||
          localStorage.getItem('userData') ||
          localStorage.getItem('user')

        if (!stored) {
          locations.value = []
          return
        }

        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) {
          locations.value = parsed
          return
        }

        const client = parsed?.user?.client || parsed?.client || parsed?.data?.client
        locations.value = client?.country?.locations || []
      } catch (err) {
        console.error('LocationPicker: failed to load locations', err)
        locations.value = []
      }
    }

    // build children (locations) from selected region ids
    const computeLocationsForRegions = (regionIds) => {
      const result = []
      const ids = Array.isArray(regionIds) ? regionIds.map(String) : [String(regionIds)]
      ids.forEach((id) => {
        const r = locations.value.find((x) => String(x.id) === String(id))
        if (r && Array.isArray(r.children)) {
          r.children.forEach((c) => {
            if (!result.some((f) => String(f.id) === String(c.id))) result.push(c)
          })
        }
      })
      locationsForSelectedRegions.value = result

      // drop any selected localLocations that don't exist anymore
      localLocations.value = (localLocations.value || []).filter((lid) =>
        locationsForSelectedRegions.value.some((l) => String(l.id) === String(lid))
      )
      emit('update:locationValue', [...localLocations.value])
    }

    // handlers
    const onRegionsChange = (val) => {
      const selected = Array.isArray(val) ? val.filter((v) => v !== null) : val ? [val] : []
      localRegions.value = selected
      emit('update:regionValue', [...localRegions.value])

      showLocations.value = locations.value.some((l) => Array.isArray(l.children) && l.children.length)
      computeLocationsForRegions(localRegions.value)

      emit('change', {
        region_ids: [...localRegions.value],
        location_ids: [...localLocations.value],
      })
    }

    const onLocationsChange = (val) => {
      const selected = Array.isArray(val) ? val.filter((v) => v !== null) : val ? [val] : []
      localLocations.value = selected
      emit('update:locationValue', [...localLocations.value])
      emit('change', {
        region_ids: [...localRegions.value],
        location_ids: [...localLocations.value],
      })
    }

    // keep in sync if parent changes props
    watch(
      () => props.regionValue,
      (nv) => {
        localRegions.value = Array.isArray(nv) ? [...nv] : []
        computeLocationsForRegions(localRegions.value)
      }
    )
    watch(
      () => props.locationValue,
      (nv) => {
        localLocations.value = Array.isArray(nv) ? [...nv] : []
      }
    )

    onMounted(() => {
      loadLocationsFromLocalStorage()
      showLocations.value = locations.value.some((l) => Array.isArray(l.children) && l.children.length)
      if (localRegions.value && localRegions.value.length) computeLocationsForRegions(localRegions.value)
    })

    return {
      locations,
      locationsForSelectedRegions,
      localRegions,
      localLocations,
      showLocations,
      onRegionsChange,
      onLocationsChange,
    }
  },
}
</script>

<style scoped>
.location-picker {
  gap: 1rem;
}

/* Fixed width for each select */
.picker-field {
  width: 250px;
}

/* vue-select styling */
.v-select {
  font-size: 0.9rem;
}

.vs__dropdown-toggle {
  min-height: 36px;
  border-color: #a29bfe;
  box-shadow: none;
}

.vs__dropdown-toggle:focus-within {
  border-color: #6c5ce7;
}

.vs__selected {
  background: #6c5ce7;
  color: white;
  border-radius: 4px;
  padding: 0 6px;
  font-size: 0.8rem;
}

@media (max-width: 768px) {
  .picker-field {
    width: 100%;
  }
}
</style>
